'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Container, Snackbar, Typography, keyframes } from '@mui/material';
import { useToastStore } from '@/stores/toast.store';
import theme from '@/app/theme';
import useCheckIsMobileSize from '@/hooks/useCheckIsMobileSize';
import { resolveToastRedirect } from '@/libs/utils/safe-redirect';

const Toast = () => {
  const router = useRouter();

  const pathname = usePathname();

  const { showToast, toastInfos, setShowToast, cleatToastInfos, anchor } = useToastStore((state) => state);

  const { text, children, redirectPathOnCloseToast = { url: '' }, onCloseToast, timeout = 2000 } = toastInfos;

  const redirectAfterCloseToast = () => {
    // 목적지 판단은 resolveToastRedirect 가 전부 한다(같은 출처 경로가 아니면 null).
    // 여기서는 그 결과만 라우터에 넘긴다 — 판단 로직을 컴포넌트 밖에 둔 덕에
    // 렌더러 없이 그대로 실행해 검증할 수 있다.
    const target = resolveToastRedirect(redirectPathOnCloseToast);
    if (!target) {
      return;
    }
    if (target.option === 'REPLACE') {
      router.replace(target.url);
    } else {
      router.push(target.url);
    }
  };

  const onClose = () => {
    if (redirectPathOnCloseToast.url !== '') {
      redirectAfterCloseToast();
    } else if (onCloseToast) {
      onCloseToast();
    }
    setShowToast(false);
    cleatToastInfos();
  };

  const clearToastAfterRouting = () => {
    if (showToast && toastInfos.redirectPathOnCloseToast) {
      setShowToast(false);
      cleatToastInfos();
    }
  };

  const handleCloseToast = () => {
    setTimeout(() => {
      if (onClose) {
        onClose();
      }
      setShowToast(false);
      cleatToastInfos();
    }, timeout);
  };

  useEffect(() => {
    if (showToast) {
      // autoHideDuration 속성 사용 대신 구현
      handleCloseToast();
    }
  }, [showToast]);

  useEffect(clearToastAfterRouting, [pathname]);

  const isMobile = useCheckIsMobileSize();

  const now = new Date().getDate();

  return (
    <Snackbar
      anchorOrigin={anchor}
      // autoHideDuration={timeout} // 실제 동작하는 데 걸리는 시간이 설정한 시간보다 훨씬 오래 걸림
      open={showToast}
      onClose={onClose}
      sx={{
        top: isMobile ? 'auto' : '0'
      }}
      key={`${(text ?? 'toast').trim()}-${now}`}
    >
      <Container
        sx={{
          whiteSpace: 'pre-wrap',
          borderRadius: '12px',
          backgroundColor: theme.palette.gray[500],
          color: 'white',
          padding: '16px',
          minWidth: '320px'
        }}
      >
        {text ? (
          <Typography variant="subtitle2" sx={{ fontFamily: theme.typography.fontFamily }}>
            {text}
          </Typography>
        ) : (
          children
        )}
      </Container>
    </Snackbar>
  );
};

export default Toast;
